/**
 * Screenshot PHI Masker
 *
 * Uses client-side OCR (Tesseract.js) to detect text in a screenshot,
 * identifies PHI patterns (patient names, MRN, DOB, SSN, phone, email),
 * and draws black redaction bars over matching text regions.
 *
 * Returns a redacted image as a data URL.
 */

// PHI patterns to match against OCR-detected text
// These are designed for individual words returned by OCR, not full text
const PHI_REGEXES: RegExp[] = [
  // Patient name patterns (multi-word — may match if OCR joins words)
  /\b(?:Mr\.|Mrs\.|Ms\.|Dr\.|Prof\.)\s+[A-Z][a-z]+(?:\s+[A-Z][a-z]+)?\b/,
  // DOB (multi-word)
  /\b(?:DOB|Date of Birth|D\.O\.B\.?)\s*[:\-]?\s*\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4}\b/i,
  // MRN (multi-word)
  /\b(?:MRN|M\.R\.N\.?|Medical Record(?:\s*Number)?|Medical Record\s*#)\s*[:\-]?\s*[A-Z0-9]{4,16}\b/i,
  // SSN — matches as a single token
  /\b\d{3}-\d{2}-\d{4}\b/,
  // Phone — matches as a single token
  /\b(?:\+?1[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}\b/,
  // Email — matches as a single token
  /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/,
  // Age — matches as a single token
  /\b\d{1,3}[-\s]?year[-\s]?old\b/i,
  // Patient: <name> (multi-word)
  /\bPatient\s*[:\-]\s*[A-Z][a-z]+(?:\s+[A-Z][a-z]+)?\b/,
  // Born / Birth date (multi-word)
  /\b(?:born|birth)\s*[:\-]?\s*\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4}\b/i,
  // ── Single-word patterns (work on individual OCR tokens) ──
  // Standalone date: 01/15/1980 or 1980-01-15
  /\b\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4}\b/,
  /\b\d{4}-\d{2}-\d{2}\b/,
  // SSN without dashes (rare but possible in OCR)
  /\b\d{9}\b/,
  // MRN number: 4-16 digit alphanumeric (common standalone format)
  /\b[A-Z]?\d{6,16}\b/,
];

// Keys near PHI that increase confidence
const PHI_CONTEXT_WORDS = [
  'patient', 'name', 'dob', 'birth', 'mrn', 'medical record',
  'ssn', 'social security', 'phone', 'email', 'address',
  'diagnosis', 'history', 'age', 'sex', 'gender',
];

export interface MaskRegion {
  x: number;
  y: number;
  width: number;
  height: number;
  text: string;
  pattern: string;
}

export interface MaskResult {
  dataUrl: string;
  maskRegions: MaskRegion[];
  totalWords: number;
  maskedWords: number;
}

/**
 * Run OCR on a screenshot and redact PHI regions.
 *
 * @param imageDataUrl - Base64 data URL of the screenshot
 * @param onProgress - Optional progress callback (0-1)
 * @returns Redacted image data URL + metadata about what was masked
 */
export async function maskScreenshotPhi(
  imageDataUrl: string,
  onProgress?: (progress: number) => void,
): Promise<MaskResult> {
  // Dynamic import to keep initial bundle size small
  const Tesseract = (await import('tesseract.js')).default;

  // Load image into an offscreen canvas
  const img = await loadImage(imageDataUrl);
  const canvas = document.createElement('canvas');
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not get canvas context');
  ctx.drawImage(img, 0, 0);

  // Run OCR
  const result = await Tesseract.recognize(canvas, 'eng', {
    logger: (m: { status: string; progress: number }) => {
      if (m.status === 'recognizing text' && onProgress) {
        onProgress(m.progress);
      }
    },
  });

  // Find PHI regions from OCR results
  // Tesseract.js types don't expose `words` on Page, so we use a typed interface
  interface OcrWord {
    text: string;
    bbox: { x0: number; y0: number; x1: number; y1: number };
  }
  const ocrData = result.data as unknown as { words?: OcrWord[] };
  const words: OcrWord[] = ocrData.words ?? [];
  const maskRegions: MaskRegion[] = [];

  for (let wordIdxCurrent = 0; wordIdxCurrent < words.length; wordIdxCurrent++) {
    const word = words[wordIdxCurrent];
    if (!word.text || word.text.trim().length === 0) continue;
    const text = word.text.trim();

    // Check if this word matches any PHI pattern
    let matchedPattern: string | null = null;
    for (const regex of PHI_REGEXES) {
      if (regex.test(text)) {
        matchedPattern = regex.source;
        break;
      }
    }

    // Check if this word is a PHI context word (e.g., "Patient", "DOB", "MRN")
    const isContextWord = PHI_CONTEXT_WORDS.some(
      (w) => text.toLowerCase().includes(w),
    );

    // If the word itself matches PHI, redact it
    if (matchedPattern) {
      maskRegions.push({
        x: word.bbox.x0,
        y: word.bbox.y0,
        width: word.bbox.x1 - word.bbox.x0,
        height: word.bbox.y1 - word.bbox.y0,
        text,
        pattern: matchedPattern,
      });
      continue;
    }

    // If this is a context word, also redact the next few words (the value)
    if (isContextWord) {
      const wordIdx = wordIdxCurrent;
      if (wordIdx + 1 < words.length) {
        // Check if this context word is followed by a colon or value
        const nextWord = words[wordIdx + 1];
        const nextText = nextWord.text?.trim() ?? '';

        // Redact the context word and the value that follows (up to 4 words)
        // Only if the next word looks like a value (not a common English word)
        const commonWords = new Set([
          'the', 'a', 'an', 'is', 'was', 'has', 'have', 'with', 'and', 'or',
          'of', 'in', 'on', 'at', 'to', 'for', 'by', 'from', 'this', 'that',
        ]);

        if (nextText && !commonWords.has(nextText.toLowerCase()) && nextText.length > 1) {
          // Redact context word
          maskRegions.push({
            x: word.bbox.x0,
            y: word.bbox.y0,
            width: word.bbox.x1 - word.bbox.x0,
            height: word.bbox.y1 - word.bbox.y0,
            text,
            pattern: 'context_word',
          });

          // Redact up to 4 following words on the same line
          for (let i = wordIdx + 1; i < Math.min(wordIdx + 5, words.length); i++) {
            const w = words[i];
            if (!w.text || w.text.trim().length === 0) continue;
            const wText = w.text.trim();
            if (commonWords.has(wText.toLowerCase())) break;

            // Only redact if on roughly the same line (y within 20px)
            if (Math.abs(w.bbox.y0 - word.bbox.y0) > 20) break;

            maskRegions.push({
              x: w.bbox.x0,
              y: w.bbox.y0,
              width: w.bbox.x1 - w.bbox.x0,
              height: w.bbox.y1 - w.bbox.y0,
              text: wText,
              pattern: 'context_value',
            });
          }
        }
      }
    }
  }

  // Draw black redaction bars over PHI regions
  ctx.fillStyle = '#000000';
  for (const region of maskRegions) {
    // Add small padding around each word
    const pad = 2;
    ctx.fillRect(
      region.x - pad,
      region.y - pad,
      region.width + pad * 2,
      region.height + pad * 2,
    );
  }

  const redactedDataUrl = canvas.toDataURL('image/png');

  return {
    dataUrl: redactedDataUrl,
    maskRegions,
    totalWords: words.filter((w: OcrWord) => w.text && w.text.trim().length > 0).length,
    maskedWords: maskRegions.length,
  };
}

/** Load an image from a data URL. */
function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}
